import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, Star, Check, Shield, Award, Lock, CheckCircle2 } from 'lucide-react';
const BADGE_URL = 'https://cdn.magicpatterns.com/uploads/sCnAJSuUH9pUX7Af6ZjA6u/Group-196.png_(1).webp';
export function Hero() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  return <section className="relative overflow-hidden bg-gradient-to-br from-cream via-white to-brand-soft/40" data-id="element-275">
      {/* Decorative background shapes (behind content) */}
      <div className="absolute inset-0 pointer-events-none z-0" aria-hidden="true" data-id="element-276">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand/5 blur-3xl" data-id="element-277"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl" data-id="element-278"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:pt-12 sm:pb-20 lg:pt-20 lg:pb-28" data-id="element-279">
        <div className="grid lg:grid-cols-12 gap-x-12 gap-y-10 lg:gap-x-16 items-start" data-id="element-280">
          {/* LEFT — Copy column */}
          <div className="lg:col-span-7" data-id="element-281">
            <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-soft text-brand text-xs font-bold uppercase tracking-[0.2em] mb-7" data-id="element-282">
              <Award size={13} strokeWidth={2.5} data-id="element-283" />
              Mitsubishi Diamond Dealer · Since 1995
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.05
          }} className="text-[34px] sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-ink-900 tracking-tighter leading-[1.08] sm:leading-[1.05]" data-id="element-284">
              Perth's <span className="text-brand" data-id="element-285">Most Trusted</span> Air
              Conditioning Installer Since 1995
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.15
          }} className="hero-sub mt-5 text-base sm:text-xl text-stone-600 max-w-2xl leading-relaxed" data-id="element-286">
              Get a fixed price quote from{' '}
              <a href="#meet-mike" className="font-bold text-ink-900 hover:text-brand" data-id="element-287">
                Mike Spear
              </a>{' '}
              personally. Mitsubishi Electric Diamond Dealer. 5.0★ from 75
              Google reviews. Workmanship guarantee on every install.
            </motion.p>

            {/* Trust proof pills */}
            <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-id="element-288">
              {[{
              icon: Check,
              label: 'Fixed price, no surprises'
            }, {
              icon: Shield,
              label: 'Owner quoted, no subbies'
            }, {
              icon: Phone,
              label: 'Same day callback'
            }, {
              icon: Award,
              label: 'Lifetime workmanship'
            }].map((item, i) => {
              const Icon = item.icon;
              return <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-stone-200 shadow-sm" data-id="element-289">
                    <span className="w-6 h-6 rounded-full bg-success/10 text-success flex items-center justify-center flex-shrink-0" data-id="element-290">
                      <Icon size={12} strokeWidth={3} data-id="element-291" />
                    </span>
                    <span className="text-xs sm:text-[13px] font-semibold text-ink-900 leading-tight" data-id="element-292">
                      {item.label}
                    </span>
                  </div>;
            })}
            </motion.div>

            {/* CTAs */}
            <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.35
          }} className="mt-8 space-y-3" data-id="element-293">
              <a href="#hero-form" className="group inline-flex items-center justify-center gap-2.5 w-full sm:w-auto h-16 px-8 rounded-xl bg-accent hover:bg-accent-600 text-ink-900 text-lg font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-cta" data-id="element-294">
                Get My Free Quote in 60 Seconds
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" strokeWidth={2.5} data-id="element-295" />
              </a>

              <a href="tel:+61864043888" className="flex items-center gap-2 text-brand text-lg font-bold hover:text-brand-700 transition-colors" data-id="element-296">
                <Phone size={18} strokeWidth={2.5} data-id="element-297" />
                <span data-id="element-298">
                  Or call Mike now:{' '}
                  <span className="tabular-nums" data-id="element-299">08 6404 3888</span>
                </span>
              </a>

              <p className="text-xs text-stone-500" data-id="element-300">
                Free quote. No obligation. No spam. No sales tactics.
              </p>
            </motion.div>

            {/* Social proof line */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.45
          }} className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 pt-6 border-t border-stone-200" data-id="element-301">
              <a href="https://www.google.com/search?q=langler+air+perth+reviews" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group" data-id="element-302">
                <div className="flex" data-id="element-303">
                  {[0, 1, 2, 3, 4].map(i => <Star key={i} size={16} className="fill-amber-400 text-amber-400" data-id="element-304" />)}
                </div>
                <span className="font-bold text-ink-900 tabular-nums group-hover:text-brand transition-colors" data-id="element-305">
                  5.0 from 75 verified Google reviews
                </span>
              </a>
              <span className="hidden sm:block w-px h-4 bg-stone-300" data-id="element-306"></span>
              <span className="text-sm font-medium text-stone-600" data-id="element-307">
                30+ years installing across Perth
              </span>
            </motion.div>

            {/* Loss-aversion (no fake A$ number) */}
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.55
          }} className="mt-5 text-sm text-stone-600 italic max-w-xl" data-id="element-308">
              An over sized or under sized system costs more to run and fails
              earlier. Mike sizes every job on site, personally.
            </motion.p>
          </div>

          {/* RIGHT — Quote form (constrained, no crash) */}
          <motion.div id="hero-form" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="lg:col-span-5 relative lg:sticky lg:top-32" data-id="element-309">
            <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold mb-3 text-center lg:text-left" data-id="element-310">
              Free Quote
            </div>

            <div className="relative max-w-md mx-auto lg:mx-0 lg:ml-auto bg-white rounded-3xl border border-stone-200 shadow-[0_30px_60px_-15px_rgba(2,110,183,0.25)] overflow-hidden" data-id="element-311">
              <div className="px-6 sm:px-7 pt-7 pb-5 border-b border-stone-200" data-id="element-312">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-soft text-brand text-[10px] font-bold uppercase tracking-wider mb-3" data-id="element-313">
                  <Award size={11} strokeWidth={2.5} data-id="element-314" />
                  Mitsubishi Diamond Dealer
                </div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-brand tracking-tight" data-id="element-315">
                  Get Your Fixed Price Quote
                </h2>
                <p className="text-sm text-stone-600 mt-1.5" data-id="element-316">
                  Same business day callback. Mike calls personally.
                </p>
                <p className="text-xs text-stone-500 mt-3 flex items-center gap-1.5" data-id="element-317">
                  <Lock size={11} data-id="element-318" /> Your info stays private. No spam. No SMS
                  marketing.
                </p>
              </div>

              {submitted ? <div className="px-6 sm:px-7 py-10 text-center" data-id="element-319">
                  <div className="w-14 h-14 rounded-full bg-success/15 text-success flex items-center justify-center mx-auto mb-4" data-id="element-320">
                    <CheckCircle2 size={28} data-id="element-321" />
                  </div>
                  <h3 className="text-xl font-extrabold text-ink-900 mb-2" data-id="element-322">
                    Got it.
                  </h3>
                  <p className="text-sm text-stone-600 mb-5" data-id="element-323">
                    Mike will call you within 2 business hours.
                  </p>
                  <a href="tel:+61864043888" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border-2 border-brand text-brand font-bold text-sm hover:bg-brand-soft transition-all" data-id="element-324">
                    <Phone size={14} data-id="element-325" /> Or call Mike now: 08 6404 3888
                  </a>
                </div> : <form onSubmit={e => {
              e.preventDefault();
              setSubmitted(true);
            }} className="px-6 sm:px-7 py-6 space-y-4" data-id="element-326">
                  <div className="flex items-center gap-2 mb-1" data-id="element-327">
                    <div className={`flex-1 h-1 rounded-full transition-colors ${step >= 1 ? 'bg-brand' : 'bg-stone-200'}`} data-id="element-328"></div>
                    <div className={`flex-1 h-1 rounded-full transition-colors ${step >= 2 ? 'bg-brand' : 'bg-stone-200'}`} data-id="element-329"></div>
                    <span className="text-[10px] font-bold text-stone-500 uppercase tracking-wider ml-1 tabular-nums" data-id="element-330">
                      Step {step}/2
                    </span>
                  </div>

                  {step === 1 ? <>
                      <FormField id="hero-name" label="Your name" placeholder="Sarah" required data-id="element-331" />
                      <FormField id="hero-phone" label="Mobile" placeholder="04XX XXX XXX" type="tel" required data-id="element-332" />
                      <FormField id="hero-suburb" label="Suburb (Perth WA)" placeholder="e.g. Karrinyup" required data-id="element-333" />

                      <button type="button" onClick={() => setStep(2)} className="w-full h-14 rounded-xl bg-accent hover:bg-accent-600 text-ink-900 font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-cta hover:scale-[1.01] active:scale-[0.99]" data-id="element-334">
                        Continue (10 seconds)
                        <ArrowRight size={16} strokeWidth={2.5} data-id="element-335" />
                      </button>
                    </> : <>
                      <div data-id="element-336">
                        <label htmlFor="hero-service" className="block text-xs font-bold text-ink-900 mb-1.5 uppercase tracking-wider" data-id="element-337">
                          Service type
                        </label>
                        <select id="hero-service" className="w-full h-12 px-4 rounded-xl bg-white border border-stone-300 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-base text-ink-900 appearance-none cursor-pointer" data-id="element-338">
                          <option data-id="element-339">I am not sure, help me decide</option>
                          <option data-id="element-340">Ducted install</option>
                          <option data-id="element-341">Split install</option>
                          <option data-id="element-342">Reverse cycle install</option>
                          <option data-id="element-343">Multi head split</option>
                          <option data-id="element-344">Commercial install</option>
                          <option data-id="element-345">Old unit removal</option>
                        </select>
                      </div>

                      <div data-id="element-346">
                        <label htmlFor="hero-message" className="block text-xs font-bold text-ink-900 mb-1.5 uppercase tracking-wider" data-id="element-347">
                          Anything else{' '}
                          <span className="text-stone-400 font-normal normal-case tracking-normal" data-id="element-348">
                            (optional)
                          </span>
                        </label>
                        <textarea id="hero-message" rows={2} placeholder="3 bed single storey, want to cool bedrooms..." className="w-full px-4 py-3 rounded-xl bg-white border border-stone-300 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-base text-ink-900 resize-none" data-id="element-349"></textarea>
                      </div>

                      <input type="hidden" name="gclid" data-id="element-350" />
                      <input type="hidden" name="utm_source" data-id="element-351" />
                      <input type="hidden" name="utm_medium" data-id="element-352" />
                      <input type="hidden" name="utm_campaign" data-id="element-353" />
                      <input type="hidden" name="utm_term" data-id="element-354" />
                      <input type="hidden" name="referrer" data-id="element-355" />
                      <input type="hidden" name="page_uri" data-id="element-356" />

                      <div className="flex gap-2" data-id="element-357">
                        <button type="button" onClick={() => setStep(1)} className="h-14 px-5 rounded-xl border-2 border-stone-300 hover:border-brand text-ink-900 font-semibold text-sm transition-all" data-id="element-358">
                          Back
                        </button>
                        <button type="submit" className="flex-1 h-14 rounded-xl bg-accent hover:bg-accent-600 text-ink-900 font-bold text-base transition-all duration-200 flex items-center justify-center gap-2 shadow-cta hover:scale-[1.01] active:scale-[0.99]" data-id="element-359">
                          Send to Mike
                          <ArrowRight size={16} strokeWidth={2.5} data-id="element-360" />
                        </button>
                      </div>
                    </>}

                  <p className="text-xs text-stone-500 leading-snug pt-1 text-center" data-id="element-361">
                    Mike calls back within 2 business hours. No spam. No SMS
                    marketing.
                  </p>
                </form>}

              <div className="hidden sm:block px-6 sm:px-7 py-4 bg-stone-50 border-t border-stone-200 rounded-b-3xl" data-id="element-362">
                <div className="text-xs text-stone-500 mb-2 font-semibold uppercase tracking-wider" data-id="element-363">
                  Authorised installer of:
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-ink-900" data-id="element-364">
                  <span data-id="element-365">Mitsubishi Electric</span>
                  <span className="text-stone-300" data-id="element-366">·</span>
                  <span data-id="element-367">Daikin</span>
                  <span className="text-stone-300" data-id="element-368">·</span>
                  <span data-id="element-369">Rinnai HRV</span>
                  <span className="text-stone-300" data-id="element-370">·</span>
                  <span data-id="element-371">iZone</span>
                </div>
                <p className="text-[10px] text-stone-500 mt-2 tabular-nums" data-id="element-372">
                  ABN 87 134 927 585 · Mon-Thu 9-4 · Fri 9-2
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function FormField({
  id,
  label,
  placeholder,
  type = 'text',
  required
}: {
  id: string;
  label: string;
  placeholder: string;
  type?: string;
  required?: boolean;
}) {
  return <div data-id="element-373">
      <label htmlFor={id} className="block text-xs font-bold text-ink-900 mb-1.5 uppercase tracking-wider" data-id="element-374">
        {label}
        {required && ' *'}
      </label>
      <input id={id} type={type} required={required} placeholder={placeholder} className="w-full h-12 px-4 rounded-xl bg-white border border-stone-300 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-base text-ink-900 placeholder:text-stone-400" data-id="element-375" />
    </div>;
}